import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  animated = true,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  animated?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [style, setStyle] = React.useState({ left: 0, width: 0 });

  const update = React.useCallback(() => {
    if (!animated) return;

    const active = ref.current?.querySelector(
      '[data-state="active"]'
    ) as HTMLElement;

    if (!active || !ref.current) return;

    setStyle({
      left: active.offsetLeft,
      width: active.offsetWidth,
    });
  }, [animated]);

  React.useEffect(() => {
    if (!animated) return;

    update();

    const resizeObserver = new ResizeObserver(update);
    const mutationObserver = new MutationObserver(update);

    if (ref.current) {
      resizeObserver.observe(ref.current);
      mutationObserver.observe(ref.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-state"],
      });
    }

    window.addEventListener("resize", update);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [update, animated]);

  return (
    <TabsPrimitive.List
      ref={ref}
      data-slot="tabs-list"
      className={cn(
        "relative bg-[#E8F1FF] dark:bg-background border inline-flex h-10 w-fit items-center justify-center rounded-full p-[3px]",
        className
      )}
      {...props}
    >
      {animated && (
        <motion.div
          className="absolute top-[3px] bottom-[3px] rounded-full bg-[#5178F5]"
          animate={style}
          transition={{ type: "spring", stiffness: 500, damping: 40 }}
        />
      )}

      {props.children}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative z-10 text-primary data-[state=active]:text-white inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer",
        className
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
